/**
 * @file store/index.ts
 * @description This file configures and creates the Redux store for the application.
 * It combines all the different reducers (slices) into a single root reducer
 * and exports the configured store, along with types for the root state and dispatch function.
 */

import { configureStore } from '@reduxjs/toolkit';
import gameReducer from './game/gameSlice';
import levelResultsReducer from './levelResults/levelResultsSlice';
import overlayReducer from './overlay/overlaySlice';
import scoringReducer from './scoring/scoringSlice';

export const store = configureStore({
  reducer: {
    game: gameReducer,
    scoring: scoringReducer,
    overlay: overlayReducer,
    levelResults: levelResultsReducer,
  },
});

/**
 * `RootState` is a type that represents the entire state of the Redux store.
 * It's inferred automatically from the store itself, so it always stays in sync
 * with the combined reducers.
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * `AppDispatch` is a type for the store's dispatch function.
 * This is used to ensure that dispatched actions are correctly typed,
 * which is especially useful when working with async thunks.
 */
export type AppDispatch = typeof store.dispatch;
